import React from 'react';
import JSONBig from 'json-bigint';
import cronstrue from 'cronstrue';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration, { DurationUnitType } from 'dayjs/plugin/duration';
import { ValidationError } from 'jsonschema';
import yaml, { YAMLException } from 'js-yaml';
import groupBy from 'lodash/groupBy';
import * as Yup from 'yup';
import type { Theme as MuiTheme } from '@mui/material/styles';

import {
  ActiveSuppressCount,
  AlertType,
  ComplianceStatusCounts,
  DetectionTestDefinition,
  LogPullingIntegration,
  OrganizationReportBySeverity,
  Permission,
  S3PrefixLogTypes,
  ScannedResources,
  Schema,
  SortDirEnum,
  SourceHealthTypesEnum,
  User,
} from 'Generated/schema';
import { PERMISSION_DEDUP_PAIRS } from 'Generated/permissionDedupPairs.generated';
import { UserDetails } from 'Source/graphql/fragments/UserDetails.generated';
import { UserFormValues } from 'Components/forms/UserForm';
import { UserInfo } from 'Components/utils/AuthContext';
import {
  ACTOR_NAME_UNKNOWN,
  ALERT_TYPE_COLOR_MAP_V3,
  CHECK_IF_HASH_REGEX,
  DEFAULT_CRON_EXPRESSION,
  INCLUDE_DIGITS_REGEX,
  INCLUDE_LOWERCASE_REGEX,
  INCLUDE_SPECIAL_CHAR_REGEX,
  INCLUDE_UPPERCASE_REGEX,
  PANTHER_CONFIG,
  PANTHER_SAML_ROLE_FROM_IDP_SUFFIX,
  PANTHER_SAML_USER_PREFIX,
  S3_OBJECT_PATH_REGEX,
  SOURCE_LABEL_REGEX,
  SYSTEM_USER_ID,
} from 'Source/constants';
import sum from 'lodash/sum';
import { ErrorResponse } from '@apollo/client/link/error';
import { ApolloError, ServerError } from '@apollo/client';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import { ListLogSources } from 'Pages/Configure/LogSources/LogSourcesPage/ListLogSources/graphql/listLogSources.generated';
import { ActorTeaser } from 'Source/graphql/fragments/ActorTeaser.generated';
import { UserTeaser } from 'Source/graphql/fragments/UserTeaser.generated';
import schemaBlueprint from 'Public/schemas/customlogs_v0_schema.json';
import { FormikValues } from 'formik';
import { SavedQueryDetails } from 'Source/graphql/fragments/SavedQueryDetails.generated';
import { APITokenTeaser } from 'Source/graphql/fragments/APITokenTeaser.generated';
import updateLocale from 'dayjs/plugin/updateLocale';

export const isMobile = /Mobi|Android/i.test(navigator.userAgent);
export const isMac = navigator.userAgent.includes('Mac OS X');
const minPassLength = PANTHER_CONFIG.COGNITO_USER_POOL_PASSWORD_MIN_LENGTH;

// Generate a new secret code that contains metadata of issuer and user email
export const formatSecretCode = (code: string, email: string): string => {
  const issuer = 'Panther';
  return `otpauth://totp/${issuer}:${email}?secret=${code}&issuer=${issuer}`;
};

export const getArnRegexForService = (awsService: string) => {
  return new RegExp(`arn:aws:${awsService.toLowerCase()}:([a-z]){2}-([a-z])+-[0-9]:\\d{12}:.+`);
};

// Derived from https://github.com/3nvi/panther/blob/master/deployments/bootstrap.yml#L557-L563
export const yupPasswordValidationSchema = Yup.string()
  .required('This field is required')
  .min(minPassLength, `Password must contain at least ${minPassLength} characters`)
  .matches(INCLUDE_UPPERCASE_REGEX, 'Password must contain at least 1 uppercase character')
  .matches(INCLUDE_LOWERCASE_REGEX, 'Password must contain at least 1 lowercase character')
  .matches(INCLUDE_SPECIAL_CHAR_REGEX, 'Password must contain at least 1 symbol')
  .matches(INCLUDE_DIGITS_REGEX, 'Password must contain at least 1 number');

export const yupIntegrationLabelValidation = Yup.string()
  .trim()
  .required('This field is required')
  .test('not-only-spaces', 'This field cannot contain only spaces', value => {
    return value ? value.trim().length > 0 : false;
  })
  .matches(SOURCE_LABEL_REGEX, 'Can only include alphanumeric characters, dashes and spaces')
  .max(36, 'Must be at most 36 characters');

export const yupWebhookValidation = Yup.string().url('Must be a valid webhook URL');

/**
 * checks whether the input is a valid UUID
 */
export const isGuid = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(str);

/**
 * caps the first letter of a string
 */
export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export function capitalizeWordsInString(string: string): string {
  return (string ?? '')
    .split(' ')
    .map(word => capitalize(word))
    .join(' ');
}

interface YupIntegrationLabelUniquenessProps {
  existingLabel: string;
  existingSources: ListLogSources['logSources'];
  methodName?:
    | 'SQS'
    | 'S3'
    | 'Log Pulling'
    | 'Eventbridge'
    | 'GCS'
    | 'Gcp Pub/Sub'
    | 'HTTP'
    | 'OTLP'
    | 'Azure Blob Storage'
    | 'Amazon Security Lake'
    | 'Azure Event Hub'
    | 'Log Forwarder';
}

export const yupIntegrationLabelUniqueness = ({
  existingLabel,
  existingSources,
  methodName = 'Log Pulling',
}: YupIntegrationLabelUniquenessProps) => {
  return yupIntegrationLabelValidation.test(
    'mutex',
    `You already have a ${methodName} source with the same label`,
    newIntegrationLabel => {
      return !existingSources?.find(
        ({ integrationLabel }) =>
          existingLabel !== newIntegrationLabel && integrationLabel === newIntegrationLabel,
      );
    },
  );
};

const withCrowdstrikeValidation = (
  schema: Yup.ArraySchema<any>,
  enableExperimentalCrowdstrikeParsing: boolean,
  initialS3PrefixLogTypes: Omit<S3PrefixLogTypes, '__typename'>[],
) => {
  const logTypeCrowdstrikeUnknown = 'Crowdstrike.Unknown';
  const logTypeCrowdstrikeFDREvent = 'Crowdstrike.FDREvent';

  return schema
    .test(
      'Crowdstrike.Unknown-deprecated',
      `${logTypeCrowdstrikeUnknown} is deprecated in favor of ${logTypeCrowdstrikeFDREvent} for new sources`,
      lTypes => {
        // Get the initial LogTypes
        const initialLogTypes = initialS3PrefixLogTypes.reduce(
          (acc, { logTypes }) => [...acc, ...logTypes],
          [],
        );

        return !(
          lTypes.includes(logTypeCrowdstrikeUnknown) &&
          !initialLogTypes.includes(logTypeCrowdstrikeUnknown)
        );
      },
    )
    .test(
      'Crowdstrike.FDREvent-not-enabled',
      `${logTypeCrowdstrikeFDREvent} not enabled yet in this instance, please contact Panther Support`,
      logTypes => {
        return !(
          logTypes.includes(logTypeCrowdstrikeFDREvent) && !enableExperimentalCrowdstrikeParsing
        );
      },
    )
    .test(
      'Crowdstrike.FDREvent-cannot-be-combined',
      `${logTypeCrowdstrikeFDREvent} cannot be combined with other log types in the same prefix`,
      logTypes => {
        return !(logTypes.includes(logTypeCrowdstrikeFDREvent) && logTypes.length > 1);
      },
    );
};

export const logTypesValidation = Yup.array(Yup.string()).test(
  'mutex',
  'AWS.SecurityFindingFormat cannot be combined with other log types',
  lTypes => {
    return !(lTypes.includes('AWS.SecurityFindingFormat') && lTypes.length > 1);
  },
);

export const requiredLogTypesValidation = logTypesValidation.required().min(1);

const excludedPrefixesValidation = () =>
  Yup.array()
    .of(Yup.string())
    .test(
      'mutex',
      'Excluded prefixes should start with included prefix',
      function validateExcludedPrefixes(excludedPrefixArray) {
        if (!this.parent.prefix) {
          return true;
        }
        // Check if every excluded prefix starts with the defined prefix and is not equal to it
        return excludedPrefixArray.every(
          (e: string) => e.startsWith(this.parent.prefix) && e !== this.parent.prefix,
        );
      },
    )
    .test(
      'mutex',
      "Excluded prefixes should not contain consecutive '*' characters",
      // eslint-disable-next-line func-names
      function (excludedPrefixArray) {
        // Check if every excluded prefix starts with the defined prefix and is not equal to it
        return excludedPrefixArray.every((e: string) => !e.includes('**'));
      },
    )
    // eslint-disable-next-line func-names
    .test('mutex', "Excluded prefixes should not end with '*'", function (excludedPrefixArray) {
      // Check if every excluded prefix starts with the defined prefix and is not equal to it
      return excludedPrefixArray.every((e: string) => !e.endsWith('*'));
    })
    .unique(`Excluded prefixes should be unique`)
    .required();

export const yupS3PrefixLogTypesValidation = (
  enableExperimentalCrowdstrikeParsing: boolean,
  s3PrefixLogTypes?: Omit<S3PrefixLogTypes, '__typename'>[],
) =>
  Yup.array()
    .of(
      Yup.object().shape({
        prefix: Yup.string()
          .test(
            'mutex',
            "'*' is not an acceptable value, leave empty if you want to include everything",
            prefix => {
              return !prefix || !prefix.includes('*');
            },
          )
          .test('mutex', "Prefix should not start with '/'", prefix => {
            return !prefix || !prefix.startsWith('/');
          }),
        excludedPrefixes: excludedPrefixesValidation(),
        logTypes: s3PrefixLogTypes
          ? withCrowdstrikeValidation(
              logTypesValidation,
              enableExperimentalCrowdstrikeParsing,
              s3PrefixLogTypes,
            )
          : logTypesValidation,
      }),
    )
    .unique(`Prefixes should be unique`, 'prefix')
    .required()
    .min(1);

export const yupCredentialsValidation = (editMode: boolean, initialCredentialsType: string) =>
  editMode
    ? Yup.string().when('credentialsType', {
        is: (val: string) => val !== initialCredentialsType,
        then: schema =>
          schema.required('New credentials must be provided when switching between auth modes'),
        otherwise: schema => schema,
      })
    : Yup.string().required();

export const yupGCSPrefixLogTypesValidation = () =>
  Yup.array()
    .of(
      Yup.object().shape({
        prefix: Yup.string()
          .test(
            'mutex',
            "'*' is not an acceptable value, leave empty if you want to include everything",
            prefix => {
              return !prefix || !prefix.includes('*');
            },
          )
          .test('mutex', "Prefix should not start with '/'", prefix => {
            return !prefix || !prefix.startsWith('/');
          }),
        excludedPrefixes: excludedPrefixesValidation(),
        logTypes: requiredLogTypesValidation,
      }),
    )
    .unique(`Prefixes should be unique`, 'prefix')
    .required()
    .min(1);

/**
 * Given a server-received DateTime string, creates a proper display text for it. We manually
 * calculate the offset cause there is no available format-string that can display the UTC offset
 * as a single digit (all of them display it either as 03:00 or as 0300) and require string
 * manipulation which is harder
 * */
export const formatDatetime = (
  datetime: string | Dayjs,
  isUTC: boolean,
  verbose = false,
  includeSeconds = false,
) => {
  // get the offset minutes and calculate the hours from them
  const utcOffset = dayjs(datetime).utcOffset() / 60;

  const suffix = isUTC
    ? 'UTC'
    : `G[M]T${utcOffset > 0 ? '+' : ''}${utcOffset !== 0 ? utcOffset : ''}`;
  const timeFormat = includeSeconds ? 'HH:mm:ss' : 'HH:mm';
  const format = verbose
    ? `dddd, DD MMMM YYYY, ${timeFormat} (${suffix})`
    : `YYYY-MM-DD ${timeFormat} ${suffix}`;

  // properly format the date
  return (isUTC ? dayjs.utc(datetime) : dayjs(datetime)).format(format);
};

/** Slice text to 7 characters, mostly used for hashIds */
export const shortenId = (id: string) => id.slice(0, 7);

/** Checking if string is a proper hash */
export const isHash = (str: string) => CHECK_IF_HASH_REGEX.test(str);

/** Converts minutes integer to representative string i.e. 15 -> 15min,  120 -> 2h */
export const minutesToString = (minutes: number) =>
  minutes < 60 ? `${minutes}min` : `${minutes / 60}h`;

/** Converts seconds to an `h m s` format */
export const secondsToHMS = (seconds: number) => {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }

  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ${seconds % 60}s`;
};

/** Converts seconds number to representative string i.e. 15 -> 15sec,  7200 -> 2 hours
 * maximumFractionDigits - Determines how many numbers to show after decimal, allows us to be
 * more intentional with how we would like to display values.
 */
export const secondsToString = (seconds: number, maximumFractionDigits = 0) => {
  const locale = window.navigator?.language || 'en-us';
  const intlOptions = {
    maximumFractionDigits,
  };

  if (seconds > 60 * 60 * 24 * 30 * 12) {
    return `${(seconds / (60 * 60 * 24 * 30 * 12)).toLocaleString(locale, intlOptions)} years`;
  }
  if (seconds > 60 * 60 * 24 * 30) {
    return `${(seconds / (60 * 60 * 24 * 30)).toLocaleString(locale, intlOptions)} months`;
  }
  if (seconds > 60 * 60 * 24) {
    return `${(seconds / (60 * 60 * 24)).toLocaleString(locale, intlOptions)} days`;
  }
  if (seconds > 60 * 60) {
    return `${(seconds / (60 * 60)).toLocaleString(locale, intlOptions)} hours`;
  }
  if (seconds > 60) {
    return `${(seconds / 60).toLocaleString(locale, intlOptions)} min`;
  }

  return `${seconds.toLocaleString(locale, intlOptions)} sec`;
};

interface AbbreviateOptions {
  divisor?: number;
  // Number of digits after the decimal point. Must be in the range 0 - 20, inclusive.
  fractionDigits?: number;
  symbols?: string[];
}

const defaultAbbreviateNumberOptions: AbbreviateOptions = {
  divisor: 1000,
  fractionDigits: 1,
  // Thousand (Kilo), Million, billion, trillion
  symbols: ['', 'K', 'M', 'B', 't'],
};

interface AbbreviateResults {
  number: number;
  suffix: string;
  // a string representing the number and suffix combined into a single string
  formatted: string;
}

/**
 * Returns an object representing a number in fixed-point notation and the suffix of your corresponding symbols.
 * @param number A JavaScript number, to be converted.
 * @param options An object that contains one or more properties. see the defaultAbbreviateNumberOptions interface for details.
 */
export const abbreviateNumber = (
  number: number,
  options?: AbbreviateOptions,
): AbbreviateResults => {
  const { divisor, fractionDigits, symbols } = {
    ...defaultAbbreviateNumberOptions,
    ...options,
  };

  const isNegativeNumber = number < 0;
  // We want to operate on the absolute value to ensure the truncating logic is still run
  // on negative numbers
  const absoluteNumber = Math.abs(number);

  if (absoluteNumber < divisor) {
    return {
      number,
      suffix: symbols[0],
      formatted: `${number}${symbols[0]}`,
    };
  }
  const k = divisor;
  const i = Math.floor(Math.log(absoluteNumber) / Math.log(k));
  const absoluteTruncatedValue = parseFloat((absoluteNumber / k ** i).toFixed(fractionDigits));
  // If the passed in number is negative we want to flip the truncatedValue that we return back to
  // negative as well
  const truncatedValue = isNegativeNumber ? -absoluteTruncatedValue : absoluteTruncatedValue;
  return {
    number: truncatedValue,
    suffix: symbols[i],
    formatted: `${truncatedValue}${symbols[i]}`,
  };
};

/** Converts bytes number to representative string i.e. 1024 -> 1 KB */
export const formatBytes = (bytes: number) => {
  if (bytes < 1) {
    return `${Math.max(bytes, 0)} B`;
  }

  const { number, suffix } = abbreviateNumber(bytes, {
    divisor: 1024,
    fractionDigits: 2,
    symbols: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'],
  });

  return [number, suffix].join(' ');
};

export const getElapsedTimeShort = (unixTimestamp: number) => {
  dayjs.extend(updateLocale);
  dayjs.extend(relativeTime);
  dayjs.updateLocale('en', {
    relativeTime: {
      // relative time format strings, keep %s %d as the same
      future: 'in %s', // e.g. in 2 hours, %s been replaced with 2hours
      past: '%s', // remove ago
      s: 'now',
      m: '1m',
      mm: '%dm',
      h: '1h',
      hh: '%dh', // e.g. 2h, %d been replaced with 2
      d: '1d',
      dd: '%dd',
      M: '1M',
      MM: '%dM',
      y: '1y',
      yy: '%dy',
    },
  });
  return dayjs.unix(unixTimestamp).fromNow();
};

/**
 * Given a server-received DateTime string, creates a proper time-ago display text for it.
 * */
export const getElapsedTime = (unixTimestamp: number) => {
  dayjs.extend(relativeTime);
  return dayjs.unix(unixTimestamp).fromNow();
};

type ElapsedTimeDurationOptions = { unit?: DurationUnitType; withSuffix?: boolean };

/**
 * Given a server-received duration integer creates a proper humanize display text for it.
 */
export const getElapsedTimeDuration = (
  value: number,
  { unit = 'seconds', withSuffix }: ElapsedTimeDurationOptions = {},
) => {
  dayjs.extend(duration);
  dayjs.extend(relativeTime);
  return dayjs.duration(value, unit).humanize(withSuffix);
};

const jsonNativeBigInt = JSONBig({ useNativeBigInt: true });

/**
 * makes sure that it properly formats a JSON struct in order to be properly displayed
 * within the editor
 * @param code valid JSON
 * @ The changed Formik values String
 */
export const formatJSON = (code: { [key: string]: unknown }) => JSON.stringify(code, null, '\t');

/**
 * makes sure that it properly formats a big int JSON struct
 * @param code valid JSON
 * @ The changed Formik values String
 */
export const formatBigIntJSON = (obj: { [key: string]: unknown }) => {
  try {
    return jsonNativeBigInt.stringify(obj, null, '\t');
  } catch (e) {
    return formatJSON(obj);
  }
};

/**
 * Parses a datalake query result row from JSON string and returns the parsed object.
 * The graph-api exposes the results both as a json and as a stringified json. We use the stringified json
 * when viewing table results to ensure that we can handle very large numbers without losing precision.
 * This could have been otherwise handled earlier, in the apollo client, if the json parser was configurable
 * in apollo-link, but unfortunately it is not.
 * https://github.com/apollographql/apollo-link/issues/924
 * @param node A stringified JSON object
 * @returns The parsed JSON object
 */
export const parseJSONWithBigInt = (node: string): any => {
  try {
    return jsonNativeBigInt.parse(node);
  } catch (e) {
    try {
      return JSON.parse(node);
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse JSON node, rendering as empty row:', node, e2);
      return {};
    }
  }
};

/**
 * makes sure that it properly formats a stringified JSON struct in order to be properly displayed within the
 * editor
 * @param str Any string that
 * @returns String
 */
export const formatStringifiedJSONSafe = (str: string, returnString = str) => {
  try {
    return formatJSON(JSON.parse(str));
  } catch (err) {
    return returnString;
  }
};

/**
 * sums up the total number of items based on the active/suppressed count breakdown that the API
 * exposes
 */
export const getComplianceItemsTotalCount = (totals: ActiveSuppressCount) => {
  return (
    totals.active.pass +
    totals.active.fail +
    totals.active.error +
    totals.suppressed.pass +
    totals.suppressed.fail +
    totals.suppressed.error
  );
};

/**
 * @param value A dictionary or object value that should be encoded
 * @return The Base64 string of the value provided
 */
export const safeBase64Encode = <T,>(value: T) => {
  return btoa(unescape(encodeURIComponent(JSON.stringify(value))));
};

/**
 * @param base64String A Base64 string that needs to be decoded
 * @return The Base64 string of the value provided
 */
export const safeBase64Decode = <T,>(base64String: string): T => {
  return JSON.parse(decodeURIComponent(escape(window.atob(base64String))));
};

/**
 * Hashes an input string and returns a digest using the corresponding algorithm.
 * @param message An input string to hash
 * @param algo The hashing algorithm to use
 * @returns Promise<ArrayBuffer>
 */
export const digestMessage = async (message: string, algo: AlgorithmIdentifier) => {
  const textEncoder = new TextEncoder();
  // Use a static 32-byte salt (base64'ed)
  // generated with crypto.randomBytes(32).toString('base64')
  const salt = 'wgpfrwo1TP8QepBsGU4AidJl0sL/3H1H9CBzpcoCLXY=';
  const data = textEncoder.encode(`${salt}${message}`);
  const digest = await window.crypto.subtle.digest(algo, data);
  return digest;
};

/**
 * Helper to take an input string and return a hashed result as a base64 string
 * @param message An input string to hash
 * @param algo The hashing algorithm to use
 * @returns  Promise<string> The hash as a base64
 */
export const hashToBase64String = async (message: string, algo: AlgorithmIdentifier) => {
  const digest = await digestMessage(message, algo);
  const ascii = new Uint8Array(digest);
  return btoa(String.fromCharCode.apply(null, ascii));
};

/**
 * sums up the total number of policies based on the severity and compliance status count breakdown
 * that the API exposes. With this function we can choose to aggregate only the failing policies
 * for a severity or even all of them, simply by passing the corresponding array of statuses to
 * aggregate.
 *
 * For example:
 * countPoliciesBySeverityAndStatus([], 'critical', ['fail', 'error']) would count the critical
 * policies that are either failing or erroring
 */
export const countPoliciesBySeverityAndStatus = (
  data: OrganizationReportBySeverity,
  severity: keyof OrganizationReportBySeverity,
  complianceStatuses: (keyof ComplianceStatusCounts)[],
) => {
  return sum(complianceStatuses.map(complianceStatus => data[severity][complianceStatus]));
};

/**
 * sums up the total number of resources based on the compliance status count breakdown
 * that the API exposes. With this function we can choose to aggregate only the failing resources
 * or even all of them, simply by passing the corresponding array of statuses to
 * aggregate.
 *
 * For example:
 * countResourcesByStatus([], ['fail', 'error']) would count the resources that are either failing
 * or erroring
 */
export const countResourcesByStatus = (
  data: ScannedResources,
  complianceStatuses: (keyof ComplianceStatusCounts)[],
) => {
  // aggregates the list of "totals" for each resourceType. The "total" for a resource type is the
  // aggregation of ['fail', 'error', ...] according to the parameter passed by the user
  return sum(
    data.byType.map(({ count }) =>
      sum(complianceStatuses.map(complianceStatus => count[complianceStatus])),
    ),
  );
};

/**
 * A function that takes the whole GraphQL error as a payload and returns the message that should
 * be shown to the user
 */
export const extractErrorMessage = (error?: ApolloError | ErrorResponse): string | undefined => {
  if (!error) {
    return undefined;
  }
  // If there is a network error show something (we are already showing the network-error-modal though)
  if (error.networkError) {
    const { statusCode } = error.networkError as ServerError;

    // If the network error has a result, check it first before deferring to hardcoded strings
    if ('result' in error.networkError) {
      // Sometimes the network error will have some errors attached to it. If it does and it's an array of 1 or more
      // items, then we should use that for the error message
      // @ts-ignore
      if (error.networkError.result?.errors?.[0]?.message) {
        // @ts-ignore
        return error.networkError.result.errors[0].message;
      }

      // Else we'll defer to the default message that the `result` key has (this is always there as part of the spec)
      // @ts-ignore
      return error.networkError.result?.message || error.networkError.message;
    }

    if (statusCode === 502) {
      return 'Request timed out';
    }

    if (statusCode === 422) {
      return 'Unable to forward request to the underlying services. Please try again';
    }

    return error.networkError.message;
  }

  // If there are no networkErrors or graphQL errors, then show the fallback
  if (!error.graphQLErrors || !error.graphQLErrors.length) {
    return 'An unexpected server error has occurred';
  }

  // isolate the first GraphQL error. Currently all of our APIs return a single error. If we ever
  // return multiple, we should handle that for all items within the `graphQLErrors` key
  const { extensions, message } = error.graphQLErrors[0];

  const statusCode = extensions?.statusCode as number | null;
  switch (statusCode) {
    case 401:
    case 403:
      return capitalize(message) || 'You are not authorized to perform this request';
    case 404:
      return capitalize(message) || "The resource you requested couldn't be found on our servers";
    default:
      return capitalize(message);
  }
};

/**
 * Formats a promise rejection or other `unknown` error for UI (e.g. `.catch(err => …)`).
 *
 * TypeScript types catch/reject values as `unknown`; Apollo may reject with {@link ApolloError},
 * but callers may also receive plain `Error` or non-Error values. Prefer this over casting to
 * `ApolloError` for {@link extractErrorMessage}.
 */
export const extractErrorMessageFromUnknown = (
  err: unknown,
  fallbackWhenEmpty = 'Unknown error',
): string => {
  if (err instanceof ApolloError) {
    return extractErrorMessage(err) ?? fallbackWhenEmpty;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallbackWhenEmpty;
};

// Copies a text to clipboard, with fallback for Safari and old-Edge
export const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
  } else {
    const container = document.querySelector('[role="dialog"] [role="document"]') || document.body;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    textArea.style.position = 'fixed'; // avoid scrolling to bottom
    container.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    container.removeChild(textArea);
  }
};

/**
 * Parses an S3 protocol URL and returns the bucket and key
 * e.g. s3://test_bucket/key_example.jsonl
 */
export const parseS3Url = (s3Url: string) => {
  const s3UrlRegex = S3_OBJECT_PATH_REGEX;
  const match = s3Url.match(s3UrlRegex);
  if (!match) {
    throw new Error(`Please provide a properly formatted S3 url`);
  }
  return {
    bucket: match[1],
    key: match[2],
  };
};

/**
 * A function that takes a text and returns a valid slug for it. Useful for filename and url
 * creation
 *
 * @param {String} text A string to parse
 * @returns {String} A slugified string
 */
export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

/**
 * Formats a file path by lowercasing all characters and replacing non-alphanumeric
 * characters (except /) with underscores. Preserves directory separators.
 *
 * @param {string} path A file path to format
 * @returns {string} A formatted file path
 * @example
 * formatFilePath('rules/AWS/My-Rule') // returns 'rules/aws/my_rule'
 * formatFilePath('rules/CloudTrail/Test@Rule#123') // returns 'rules/cloudtrail/test_rule_123'
 */
export const formatFilePath = (path: string): string => {
  if (!path) {
    return '';
  }
  return path
    .split('/')
    .map(segment => {
      // Convert to lowercase and replace non-alphanumeric characters (except /) with underscores
      return segment.toLowerCase().replace(/[^a-z0-9]/g, '_');
    })
    .join('/');
};

export const isNumber = (value: string) => /^-{0,1}\d+$/.test(value);

/**
 * A function that returns true if the string consists of only non-whitespace characters
 * @param {string} value A string to test
 */
export const hasNoWhitespaces = (value: string) => /^\S+$/.test(value);

export const toStackNameFormat = (val: string) => val.replace(/ |_/g, '-').toLowerCase();

/**
 * Builds the CLI command for deploying a CloudFormation stack from a downloaded
 * template file. Used by lookup table IAM setup and managed bucket notification
 * setup flows.
 */
export const getCfnDeployCommand = (stackName: string) =>
  `aws cloudformation deploy --template-file ./${stackName}.yml --stack-name ${stackName} --capabilities CAPABILITY_NAMED_IAM`;

/*
Given a list of permissions, returns the actual permissions that should be activated. For
example, if `ModifyUser` is active, then `ReadUser` should automatically be active as well.
Similarly, `ManageAIResponses` implies `ViewAIPrivateResponses`.

Auto-synced from Go via 'mage gen:permissionDedupPairs'. To add a new implication,
edit dedupPairs in pkg/permissions/dedup.go and re-run the generator.
 */
export const expandPermissions = (rolePermissions: Permission[]) => {
  const expanded = new Set(rolePermissions);
  // Order matters: transitive chains (e.g. AIRunAsModify → RunPantherAI → ViewAISkills)
  // resolve correctly because the generated array preserves Go's ordering.
  PERMISSION_DEDUP_PAIRS.forEach(pair => {
    if (expanded.has(pair.modify as Permission)) {
      expanded.add(pair.read as Permission);
    }
  });
  return [...expanded];
};

/*
Given a list of permissions, deduplicates implied permissions. For
example, if `ModifyUser` and `ReadUser` are present, then `ReadUser` should be removed.

Auto-synced from Go via 'mage gen:permissionDedupPairs'. To add a new implication,
edit dedupPairs in pkg/permissions/dedup.go and re-run the generator.
 */
export const dedupPermissions = (rolePermissions: Permission[]) => {
  const deduped = new Set(rolePermissions);
  PERMISSION_DEDUP_PAIRS.forEach(pair => {
    if (deduped.has(pair.read as Permission) && deduped.has(pair.modify as Permission)) {
      deduped.delete(pair.read as Permission);
    }
  });
  return [...deduped];
};

/**
 * Check whether a Cognito user is coming from SAML
 */

export const isCognitoSamlUser = (userInfo?: Partial<UserInfo> & Pick<UserInfo, 'identities'>) => {
  if (!userInfo) {
    return false;
  }

  const { identities } = userInfo;
  if (!identities) {
    return false;
  }
  // For reference and backwards compatible reasons identities may come up as a string from Cognito or the localstorage cache.
  // thus we need to ensure that everything works as expected.
  let parsedIdentities;
  try {
    if (isString(identities)) {
      parsedIdentities = JSON.parse(identities);
    } else if (isArray(identities)) {
      parsedIdentities = identities;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
  return parsedIdentities.some(identity => identity?.providerName === PANTHER_SAML_USER_PREFIX);
};

/**
 * Check whether a user is coming from SAML
 */
export const isSamlUser = (
  user: Partial<User | UserDetails | UserFormValues> &
    Pick<User | UserDetails | UserFormValues, 'id'>,
) => {
  const { id } = user;
  return id?.startsWith(PANTHER_SAML_USER_PREFIX);
};

/**
 * Check whether a user is coming from SAML
 */
export const hasRoleFromIdp = (
  user: Partial<User | UserDetails | UserFormValues> &
    Pick<User | UserDetails | UserFormValues, 'role'>,
) => {
  const { role } = user;
  return role?.name?.endsWith(PANTHER_SAML_ROLE_FROM_IDP_SUFFIX);
};

/*
Given a user, returns a human-readable string to show for the user's name
*/
export const getActorDisplayName = (actor: ActorTeaser, firstNameOnly?: boolean) => {
  if (!actor) {
    return ACTOR_NAME_UNKNOWN;
  }

  const { email, enabled, familyName, givenName, id } = actor as UserTeaser;

  // See the various types of users returned from `resolvers/user_helpers.go`
  // `enabled` is false if user is deleted or deactivated
  // `givenName` will be `Deleted` for hard deleted users
  // `email` is non-existent for API tokens
  // If an Email is NOT present then NO suffix is added
  let deactivatedSuffix = '';
  if (!enabled && email && givenName !== 'Deleted' && id !== SYSTEM_USER_ID) {
    deactivatedSuffix = ' (Deactivated)';
  }

  if (firstNameOnly) {
    return `${givenName || ''}${deactivatedSuffix}`;
  }

  if (givenName && familyName) {
    return `${givenName} ${familyName}${deactivatedSuffix}`;
  }

  const displayName = givenName || familyName || email || (actor as APITokenTeaser).name;
  return `${displayName}${deactivatedSuffix}`;
};

/** Returns a display string for a user combobox item (either a plain string sentinel or a UserDetails-like object). */
export const getUserItemString = (item: string | ActorTeaser): string => {
  if (typeof item === 'string') {
    return item;
  }
  return getActorDisplayName(item);
};

/**
 * Generates a random HEX color
 */
export const generateRandomColor = () => Math.floor(Math.random() * 16777215).toString(16);

/**
 * Converts a rem measurement (i.e. `0.29rem`) to pixels. Returns the number of pixels
 */
export const remToPx = (rem: string) => {
  return parseFloat(rem) * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

/**
 * Appends a trailing slash if missing from a url.
 *
 * @param {String} url A URL to check
 * @returns {String} A URL with a trailing slash
 */
export const addTrailingSlash = (url: string) => {
  return url.endsWith('/') ? url : `${url}/`;
};

/**
 * Generating an OAuth link for an integration
 */
export const generateIntegrationOAuthLink = (integrationId: string, redirectUri: string) => {
  // Success Redirect url for successful authorization
  const successUrl = encodeURIComponent(`${window.location.origin}${redirectUri}`);
  // Public endpoint for authorization an oauth app via the backend
  return `https://${PANTHER_CONFIG.PUBLIC_CORE_API_HOST}/sources-oauth2/authorize?id=${integrationId}&success_url=${successUrl}`;
};

/**
 * Checks whether a log source is missing OAuth authorization
 */
export const checkIfSourceIsAuthorized = (source: Pick<LogPullingIntegration, 'oauth2'>) => {
  if (!source) {
    return false;
  }

  return !source.oauth2?.mustAuthorize;
};

/**
 * OAuth redirect url needed for whitelisting in some integration i.e. Box
 */
export const OAuthRedirectUrl = `https://${PANTHER_CONFIG.PUBLIC_CORE_API_HOST}/sources-oauth2/token_redirect`;

/**
 * Interface for Indicator Search parameters for use in IndicatorSearch and DataExplorer
 */
export interface IndicatorUrlParams {
  snippedId: string;
  databases?: string[];
  tables?: string[];
  endTime: string;
  startTime: string;
  indicatorName: string;
  i: string[];
  execute?: boolean;
}

/**
 * Strips hashes and query params from a URI, returning the pathname
 *
 * @param {String} uri A relative URI
 * @returns {String} The same URI stripped of hashes and query params
 */
export const getPathnameFromURI = (uri: string) => uri.split(/[?#]/)[0];

export const getCurrentYear = () => {
  return dayjs().format('YYYY');
};

export const getGraphqlSafeDateRange = ({
  days = 0,
  hours = 0,
}: {
  days?: number;
  hours?: number;
}) => {
  const utcNow = dayjs.utc();
  const utcDaysAgo = utcNow.subtract(days, 'day').subtract(hours, 'hour');

  // the `startOf` and `endOf` help us have "constant" inputs for a few minutes, when we are using
  // those values as inputs to a GraphQL query. Of course there are edge cases.
  return [
    utcDaysAgo.startOf('hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
    utcNow.endOf('hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
  ];
};

// FIXME: Remove function when we have better alert filtering
export const getGraphqlSafeDateRangeFuture = ({
  days = 0,
  hours = 0,
}: {
  days?: number;
  hours?: number;
}) => {
  const utcNow = dayjs.utc();
  const utcFuture = utcNow.add(1, 'day');
  const utcDaysAgo = utcNow.subtract(days, 'day').subtract(hours, 'hour');

  // the `startOf` and `endOf` help us have "constant" inputs for a few minutes, when we are using
  // those values as inputs to a GraphQL query. Of course there are edge cases.
  return [
    utcDaysAgo.startOf('hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
    utcFuture.endOf('hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
  ];
};

export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num);
};

/**
 *
 * Downloads the blob after converting string to blob if needed as a file
 *
 * @param data Blob or string data to download
 * @param filename The name to save it under, along  with the extension. i.e. file.csv
 *
 */
export const downloadData = (data: Blob | string, filename: string) => {
  let blob;
  if (data instanceof Blob) {
    blob = data;
  } else {
    const extension = filename.split('.')[1];
    blob = new Blob([data], {
      type: `text/${extension};charset=utf-8`,
    });
  }
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
};

/**
 *
 * Converting base64 string data to blob
 *
 * @param base64 The base64 data to convert.
 *
 */
export const base64ZipToBlob = (base64: string) => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;

  const ab = new ArrayBuffer(binaryLen);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < binaryLen; i += 1) {
    ia[i] = binaryString.charCodeAt(i);
  }
  return new Blob([ab]);
};

/**
 * Helper function that return key from Enumaration value
 * @param object
 * @param value
 */
export function getEnumKeyByValue(object: { [key: string]: string }, value: string) {
  return Object.keys(object).find(key => object[key] === value);
}

/**
 * Helper function that returns proper text for Alert Type
 */
export const alertTypeToString = (type: AlertType, { useSingular = false } = {}) => {
  switch (type) {
    case AlertType.Rule:
      return useSingular ? 'Rule Match' : 'Rule Matches';
    case AlertType.RuleError:
      return useSingular ? 'Rule Error' : 'Rule Errors';
    case AlertType.CorrelationRule:
      return useSingular ? 'Correlation Rule Match' : 'Correlation Rule Matches';
    case AlertType.CorrelationRuleError:
      return useSingular ? 'Correlation Rule Error' : 'Correlation Rule Errors';
    case AlertType.Policy:
      return useSingular ? 'Policy Failure' : 'Policy Failures';
    case AlertType.SystemError:
      return useSingular ? 'System Error' : 'System Errors';
    case AlertType.ScheduledRule:
      return useSingular ? 'Scheduled Rule Match' : 'Scheduled Rule Matches';
    case AlertType.ScheduledRuleError:
    default:
      return useSingular ? 'Scheduled Rule Error' : 'Scheduled Rule Errors';
  }
};

export const cleanUpEmptyDetectionTestMocks = (
  tests: DetectionTestDefinition[],
): DetectionTestDefinition[] => {
  return tests.map(({ mocks = [], ...rest }) => ({
    ...rest,
    mocks: mocks.filter(mock => !!mock.objectName),
  }));
};

/**
 * Add values to cron expression
 */
export const formatCronExpression = (values: {
  minutes?: string;
  hours?: string;
  days?: string;
  months?: string;
  weekDays?: string;
}) => {
  return `${values.minutes} ${values.hours} ${values.days} ${values.months} ${values.weekDays}`;
};

/**
 * Get default cron values
 * @param cronExpression
 */

export const splitCronValues = (cronExpression = DEFAULT_CRON_EXPRESSION) => {
  const [minutes, hours, days, months, weekDays] = cronExpression.split(' ');
  return [minutes, hours, days, months, weekDays];
};

export const partialCronValidate = ({
  minutes = '*',
  hours = '0',
  days = '*',
  months = '*',
  weekdays = '*',
}) => {
  try {
    cronstrue.toString(`${minutes} ${hours} ${days} ${months} ${weekdays}`);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Converts a word to its plural form
 *
 * @returns {String} pluralized word
 *
 * @example
 * toPlural('example'); // => 'examples'
 * toPlural('example', 10); // => 'examples'
 * toPlural('example', 1); // => 'example'
 * toPlural('example', 'examplez', 10); // => 'examplez'
 * toPlural('example', 'examplez', 1); // => 'example'
 */
function toPlural(word: string): string;
function toPlural(word: string, count: number): string;
function toPlural(word: string, pluralForm: string, count: number): string;
function toPlural(word: string, pluralFormOrCount?: number | string, count?: number) {
  const plrl = typeof pluralFormOrCount === 'string' ? pluralFormOrCount : undefined;
  const cnt = typeof pluralFormOrCount === 'number' ? pluralFormOrCount : count;

  const pluralForm = plrl || `${word}s`;

  return cnt === 1 ? word : pluralForm;
}
export { toPlural };

/**
 * Compares two semver version
 * returns 1 if a version is bigger than b
 * returns -1 if a version is smaller than b
 * returns 0 if a version is equal to b
 * @returns Number [1,0,-1]
 */
export function compareSemanticVersion(a: string, b: string) {
  const av = a.match(/([0-9]+|[^0-9]+)/g);
  const bv = b.match(/([0-9]+|[^0-9]+)/g);
  for (;;) {
    let ia = av.shift();
    let ib = bv.shift();
    if (typeof ia === 'undefined' && typeof ib === 'undefined') {
      return 0;
    }
    if (typeof ia === 'undefined') {
      ia = '';
    }
    if (typeof ib === 'undefined') {
      ib = '';
    }

    const ian = parseInt(ia, 10);
    const ibn = parseInt(ib, 10);
    if (Number.isNaN(ian) || Number.isNaN(ibn)) {
      // non-numeric comparison
      if (ia < ib) {
        return -1;
      }
      if (ia > ib) {
        return 1;
      }
    } else {
      if (ian < ibn) {
        return -1;
      }
      if (ian > ibn) {
        return 1;
      }
    }
  }
}

type SortingType = { sortBy?: unknown; sortDir?: SortDirEnum };

export type WrapSortingFormValues<T extends SortingType> = Omit<T, 'sortBy' | 'sortDir'> & {
  sorting?: string;
};

export interface SortingOption<T extends SortingType> {
  opt: string;
  resolution: Pick<T, 'sortBy' | 'sortDir'>;
}

/**
 * Since `sortBy` and `sortDir` parameters are not responding to any form
 * values we should extract them from the `sorting` combobox value.
 */
export const extractSortingOptions = <T,>(
  values: WrapSortingFormValues<T>,
  sortingOptions: SortingOption<T>[],
) => {
  const { sorting, ...rest } = values;
  const sortingParams = sortingOptions.find(param => param.opt === sorting);
  return {
    ...rest,
    ...(sortingParams?.resolution ?? { sortBy: null, sortDir: null }),
  };
};

/**
 * Since `sorting` is not responding to some Input key we shall extract
 * this information from `sortBy` and `sortDir` parameters in order to align the
 * combobox values.
 */
export const wrapSortingOptions = <T extends SortingType>(
  params: T,
  sortingOptions: SortingOption<T>[],
): WrapSortingFormValues<T> => {
  const { sortBy, sortDir, ...rest } = params;
  const option = sortingOptions.find(
    param => param.resolution.sortBy === sortBy && param.resolution.sortDir === sortDir,
  );

  return {
    ...(option ? { sorting: option.opt } : {}),
    ...rest,
  };
};

export const tryOrNoop = (fn: () => any) => {
  try {
    fn();
  } catch (err) {
    // noop
  }
};

// Schema utils
export const DATA_SCHEMA_ID_PREFIX = 'Custom.';
export const stripDataSchemaPrefix = (name = '') => name.replace(DATA_SCHEMA_ID_PREFIX, '');
export const appendDataSchemaPrefix = (name = '') => `${DATA_SCHEMA_ID_PREFIX}${name}`;

export type DataSchemaErrors = Record<string, (ValidationError | YAMLException)[]>;

export const validateDataSchema = async (schema: string): Promise<DataSchemaErrors> => {
  const { Validator } = await import(/* webpackChunkName: "json-schema-validation" */ 'jsonschema');
  try {
    const validator = new Validator();
    const schemaAsObject = yaml.load(schema);
    const result = validator.validate(schemaAsObject, schemaBlueprint as any, {
      propertyName: 'root',
    });

    if (!result.errors.length) {
      return {};
    }
    // Removes un-necessary errors that are bloating the UI
    const withoutSchemaAllOfErrors = result.errors.filter(err => err.name !== 'allOf');

    // Group errors by their associated field
    const errorsByField = groupBy(withoutSchemaAllOfErrors, err => err.property);
    return errorsByField;
  } catch (err) {
    const { name, message, reason, mark } = err as YAMLException;
    return {
      [name]: [{ name, message, reason, mark }],
    };
  }
};

export const dataSchemaNameValidation = (existingLogTypes: string[] = []) => {
  return (
    Yup.string()
      .required()
      // When we create schema in AWS Glue to query in Athena.
      // A table name cannot be longer than 255 characters.
      // 255 - SCHEMA_ID_PREFIX.length
      .max(248)
      .matches(/^[A-Z]/, 'Must start with a capital letter. For example: Kubernetes')
      .matches(
        /^([A-Z][A-Za-z0-9]+)(\.[A-Z][A-Za-z0-9]+){0,5}$/,
        'Every word must start with a capital letter followed by alphanumeric characters and a dot can be used as a word delimiter. For example: Custom.Kubernetes.Audit',
      )
      .test(
        'mutex',
        `You already have a Schema with the same name`,
        (name: string) =>
          !existingLogTypes
            .map(e => e.toLowerCase())
            ?.includes(appendDataSchemaPrefix(name).toLowerCase()),
      )
  );
};

export const dataSchemaValidation = (
  existingLogTypes: string[],
  withExtendedSpecValidation = false,
) => {
  const baseSchema = Yup.object({
    name: dataSchemaNameValidation(existingLogTypes),
    revision: Yup.number(),
    description: Yup.string(),
    referenceURL: Yup.string().url(),
    spec: Yup.string().when('isFieldDiscoveryEnabled', {
      is: true, // alternatively: (val) => val == true
      then: schema => schema,
      otherwise: schema => schema.required(),
    }),
    isFieldDiscoveryEnabled: Yup.boolean(),
  });

  if (!withExtendedSpecValidation) {
    return baseSchema;
  }

  const noHeaderColumnsMessage = 'Add at least one column when the CSV has no header';

  const expandFieldsValidation = Yup.object().test(
    'keys',
    'All keys must be non-empty strings containing only alphanumeric characters',
    obj => {
      if (!obj) {
        // If field5 is undefined or null, validation passes
        return true;
      }
      const keys = Object.keys(obj);
      return keys.every(key => /^[a-zA-Z][a-zA-Z0-9]*$/.test(key));
    },
  );

  return baseSchema.shape({
    parserType: Yup.string().oneOf(['csv', 'fastmatch', 'regex', 'script', 'none']),
    parser: Yup.object()
      .when('parserType', {
        is: 'csv',
        then: Yup.object().shape({
          csv: Yup.object({
            hasHeader: Yup.boolean().required(),
            delimiter: Yup.string().oneOf([',', '\t', ' ', '|']).when('hasHeader', {
              is: false,
              then: Yup.string().required(),
            }),
            // Columns are required only when there is no header; keep min(1) inside the `when` branch.
            columns: Yup.array()
              .of(Yup.string())
              .when('hasHeader', {
                is: false,
                then: schema =>
                  schema.min(1, noHeaderColumnsMessage).required(noHeaderColumnsMessage),
              }),
            skipPrefix: Yup.string(),
            emptyValues: Yup.array().of(Yup.string()).nullable(),
            trimSpace: Yup.boolean(),
            expandFields: expandFieldsValidation,
          }),
        }),
      })
      .when('parserType', {
        is: 'fastmatch',
        then: Yup.object().shape({
          fastmatch: Yup.object({
            match: Yup.array()
              .of(
                Yup.string()
                  .required()
                  .min(4, 'Must be at least 4 characters long')
                  .matches(/%\{[^}]+\}/, 'Must contain at least one placeholder %{...}'),
              )
              .min(1)
              .required(),
            skipPrefix: Yup.string(),
            emptyValues: Yup.array().of(Yup.string()).nullable(),
            trimSpace: Yup.boolean(),
            expandFields: expandFieldsValidation,
          }),
        }),
      })
      .when('parserType', {
        is: 'regex',
        then: Yup.object().shape({
          regex: Yup.object({
            patternDefinitions: Yup.object().test(
              'keys',
              'All pattern names must start with a capital letter and contain only alphanumeric capital characters',
              obj => {
                if (!obj) {
                  return true;
                }
                const keys = Object.keys(obj);
                return keys.every(key => /^[A-Z][A-Z0-9]*$/.test(key));
              },
            ),
            match: Yup.array().of(Yup.string()).min(1).required(),
            skipPrefix: Yup.string(),
            emptyValues: Yup.array().of(Yup.string()).min(1),
            trimSpace: Yup.boolean(),
            expandFields: Yup.object(),
          }),
        }),
      })
      .when('parserType', {
        is: 'script',
        then: Yup.object({
          script: Yup.object({
            function: Yup.string().required(),
          }),
        }),
      }),
  });
};

/**
 * FileReader Utils
 */
export const fileToText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = e => resolve(e.target.result as string);
    reader.onerror = error => reject(error);
  });

export const stripNullValuesFromJSON = (data: Record<string, unknown>) =>
  Object.keys(data)
    .filter(key => !(data[key] === null || data[key] === undefined))
    .reduce((acc, k) => {
      acc[k] = data[k];
      return acc;
    }, {});

export const typedMemo: <T>(c: T) => T = React.memo;

export const countLineBreaks = (str: string) => {
  /* counts \n */
  try {
    const numLines = str.split('\n').length;
    // num line breaks == num lines - 1
    // (eg 2 lines implies 1 line break in between)
    return numLines > 0 ? numLines - 1 : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Formik Utils
 */

/**
 * A function that returns an object with the changed formik values.
 * This is a shallow check, so if we change a nested array or object,
 * the changed value will always be present
 * @param values Form values
 * @param initialValues The initial values of the form
 * @returns The changed Formik values
 */
export const getChangedValues = (
  values: FormikValues,
  initialValues: FormikValues,
): FormikValues => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const hasChanged = initialValues[key] !== value;

    if (hasChanged) {
      acc[key] = value;
    }

    return acc;
  }, {});
};

/**
 * Given a saved search, determine whether a warning should be shown about the
 * search requiring a LIMIT clause.
 */
export const shouldShowLimitClauseWarning = (query: SavedQueryDetails, sql = query.sqlQuery) => {
  const requiresLimitClause = query?.requiresLimitClause && query?.schedule;
  return Boolean(requiresLimitClause && sql && !sql.match(/limit/i));
};

/**
 * Given a SQL script, determine whether a warning should be shown about the
 * search requiring a time range (either p_occurs_since or p_occurs_between).
 */
export const shouldShowTimeRangeClauseWarning = (sql = '') => {
  return Boolean(
    sql &&
    !(
      sql.match(/p_occurs_since/i) ||
      sql.match(/p_occurs_between/i) ||
      sql.match(/p_occurs_around/i) ||
      sql.match(/p_event_time/i)
    ),
  );
};

// Returns the origin of a log type. Used to group schemas in the dropdown field
// Amazon schemas should be listed under the AWS group.
export const getLogTypeProvider = (schema: string) => {
  const origin = schema.split('.')[0];

  return origin === 'Amazon' ? 'AWS' : origin;
};

/**
 * Truncate a string in the middle
 */
export const truncateMiddle = (
  text: string,
  startChars: number,
  endChars: number,
  maxLength: number,
  omission = '...',
): string => {
  if (text.length > maxLength) {
    const start = text.substring(0, startChars);
    const end = text.substring(text.length - endChars, text.length);
    return `${start}${omission}${end}`;
  }
  return text;
};

/**
 * Debounce promise
 * There is a limitation to the lodash debounce
 * when used with promises
 * ref: https://github.com/lodash/lodash/issues/4400
 */
export const debouncePromise = (fn, ms = 0) => {
  let timeoutId;
  const pending = [];
  return (...args) =>
    new Promise((res, rej) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const currentPending = [...pending];
        pending.length = 0;
        Promise.resolve(fn.apply(this, args)).then(
          data => {
            currentPending.forEach(({ resolve }) => resolve(data));
          },
          error => {
            currentPending.forEach(({ reject }) => reject(error));
          },
        );
      }, ms);
      pending.push({ resolve: res, reject: rej });
    });
};

/**
 * @function logTypeToTable
 * @param logType - the log type to convert to a table name
 * @returns the table name for a given log type
 */
export const logTypeToTable = (logType: string) => {
  // the name of the table for the given log type
  // ref: https://github.com/panther-labs/panther-enterprise/blob/d5efbb2c16144bffdd8f3684d159f524e3e5bf68/internal/log_analysis/pantherdb/pantherdb.go#L128
  // Matches backend's CanonicalTableName:
  //   1. replDotNDash: ".", "-", " " -> "_"
  //   2. replNonWords: remove all non-word characters ([^\w])
  //   3. replBadStart: remove leading underscores and numerics
  return logType
    ?.replace(/[.\- ]/g, '_')
    .replace(/[^\w]/g, '')
    .replace(/^[_0-9]+/, '')
    .toLowerCase();
};

/**
 * Changes a indicator type fields to a friendlier display
 * @param type
 */
export const indicatorTypeToString = (type: string) => {
  const convertWordCase = (word: string) =>
    ({
      ip: 'IP',
      ids: 'IDs',
      arns: 'ARNs',
      aws: 'AWS',
      md5: 'MD5',
      sha1: 'SHA-1',
      sha256: 'SHA-256',
    })[word] ?? capitalize(word);

  if (type.includes('p_any')) {
    return type.replace('p_any_', '').split('_').map(convertWordCase).join(' ');
  }

  return type.match(/[A-Z][a-z]+/g).join(' ');
};

/**
 * Gets the target value in a nested object, based on the given key
 * @function getNestedValue
 * @param obj - the object
 * @param target - the given key
 * @returns the target value
 */
export const getNestedValue = (obj: Record<string, any>, target: string) => {
  if (!obj || (typeof obj !== 'object' && !Array.isArray(obj))) {
    return undefined;
  }
  if (target in obj) {
    return obj[target];
  }
  if (Array.isArray(obj)) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < obj.length; i++) {
      const result = getNestedValue(obj[i], target);
      if (result !== undefined) {
        return result;
      }
    }
  } else {
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const k in obj) {
      const result = getNestedValue(obj[k], target);
      if (result !== undefined) {
        return result;
      }
    }
  }

  return undefined;
};

// TODO: check if this is still works with really nested schemas
export const getNestedValueOfYamlSchema = (schema: string, target: string) => {
  try {
    const schemaAsObject = yaml.load(schema) as Schema;
    const { fields } = schemaAsObject;
    return getNestedValue(fields, target);
  } catch {
    return undefined;
  }
};

export const mergeSchemaObjectsWithDiscovery = (
  original: Record<string, any>,
  discoveredObject: Record<string, any>,
): Record<string, any> => {
  // Create a copy of the original object
  const mergedObject = { ...original };

  // If the original object doesn't have fields, create an empty array
  if (!mergedObject.fields) {
    mergedObject.fields = [];
  }

  // Recursively merge the fields
  function mergeFields(source, target) {
    // If the source doesn't have fields, return
    // we don't want to merge anything
    if (!source?.fields?.length) {
      return;
    }

    // For each field in the source, check if it exists in the target
    source.fields.forEach(newField => {
      const existingField = target.fields.find(field => field.name === newField.name);

      if (existingField) {
        if (newField.type === 'object' && existingField.type === 'object') {
          if (newField.fields) {
            if (!existingField.fields) {
              existingField.fields = [];
            }
            mergeFields(newField, existingField);
          }
        } else if (newField.type === 'array' && existingField.type === 'array') {
          if (newField.element && existingField.element) {
            // If the element is an object, merge the fields
            // Otherwise, just keep the existing element
            if (newField.element.type === 'object' && existingField.element.type === 'object') {
              mergeFields(newField.element, existingField.element);
            }
          }
        }
      } else {
        target.fields.push({ ...newField, isDiscovered: true });
      }
    });
  }

  mergeFields(discoveredObject, mergedObject);
  return mergedObject;
};

export const mergeYamlSchemasWithDiscovery = (schema: string, discoveredSchema: string) => {
  try {
    const original = yaml.load(schema);
    const discovered = yaml.load(discoveredSchema);
    const mergedObject = mergeSchemaObjectsWithDiscovery(original, discovered);
    const newSchema = yaml.dump(mergedObject);
    return newSchema;
  } catch {
    return undefined;
  }
};

// Redirect to health page only for actionable alarms
// have a look here: https://github.com/panther-labs/panther-enterprise/blob/7d80b80dd7cc6cb3dc00a8665b5d6e250baeb413/api/lambda/source/models/integration.go#L531
export const isActionableAlarm = (type: SourceHealthTypesEnum) =>
  type === SourceHealthTypesEnum.LogProcessingErrorsStatus ||
  type === SourceHealthTypesEnum.ClassificationFailuresStatus ||
  type === SourceHealthTypesEnum.SystemAlarmStatus;

// Helper function to get the color for a given alert type to work with Pounce V2 token system
export const getAlertTypeColorV3 = (alertType: AlertType, theme: MuiTheme): string => {
  return ALERT_TYPE_COLOR_MAP_V3[alertType](theme);
};
